'use client';

import { createClient, isSchemaError } from '@/lib/supabase/client';
import axios from 'axios';

export interface MarketIndex {
  id: string;
  name: string;
  value: number;
  change: number;
  changePercent: number;
  high: number;
  low: number;
}

export interface Commodity {
  id: string;
  name: string;
  symbol: string;
  price: number;
  value: number;
  change: number;
  changePercent: number;
  unit: string;
  icon: string;
  category: string;
  updatedAt: string;
}

export interface CityRate {
  id: string;
  city: string;
  gold22k: number;
  gold24k: number;
  silver: number;
  change22k: number;
  change24k: number;
  changeSilver: number;
}

export interface MarketData {
  id: string;
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  volume: string;
  marketCap: string;
}

export interface NewsArticle {
  id: string;
  title: string;
  source: string;
  time: string;
  category: string;
  image: string;
  alt: string;
  impact: 'high' | 'medium' | 'low';
  excerpt: string;
  url?: string;
}

// Real-time API configuration
const API_KEY = process.env.NEXT_PUBLIC_FINANCIAL_API_KEY || '';
const API_BASE_URL = process.env.NEXT_PUBLIC_FINANCIAL_API_URL || '';

// Cache for API responses to reduce API calls
const apiCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 60000; // 1 minute cache

// Real-time API methods
export const realtimeAPI = {
  async fetchLiveMarketData(symbol: string): Promise<any> {
    const cacheKey = `market_${symbol}`;
    const cached = apiCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      // Example API call structure - adapt based on actual API provider
      const response = await axios.get(`${API_BASE_URL}/quote`, {
        params: {
          symbol,
          apikey: API_KEY,
        },
        timeout: 5000,
      });

      apiCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch live data for ${symbol}:`, error);
      return null;
    }
  },

  async fetchMultipleSymbols(symbols: string[]): Promise<Map<string, any>> {
    const results = new Map<string, any>();
    
    // Fetch in parallel with rate limiting
    const promises = symbols.map(async (symbol) => {
      const data = await this.fetchLiveMarketData(symbol);
      if (data) {
        results.set(symbol, data);
      }
    });

    await Promise.allSettled(promises);
    return results;
  },

  async fetchCommodityPrices(): Promise<{ gold: number; silver: number; crude: number } | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/commodities`, {
        params: { apikey: API_KEY },
        timeout: 5000,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch commodity prices:', error);
      return null;
    }
  },

  async fetchForexRates(): Promise<Map<string, number>> {
    const rates = new Map<string, number>();
    try {
      const response = await axios.get(`${API_BASE_URL}/forex`, {
        params: { apikey: API_KEY },
        timeout: 5000,
      });
      
      if (response.data) {
        Object.entries(response.data).forEach(([pair, rate]) => {
          rates.set(pair, rate as number);
        });
      }
    } catch (error) {
      console.error('Failed to fetch forex rates:', error);
    }
    return rates;
  },
};

export const financialDataService = {
  async getMarketIndices(): Promise<MarketIndex[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('market_indices')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (
        data?.map((row) => ({
          id: row.id,
          name: row.name,
          value: parseFloat(row.value),
          change: parseFloat(row.change),
          changePercent: parseFloat(row.change_percent),
          high: parseFloat(row.high),
          low: parseFloat(row.low),
        })) || []
      );
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getCommodities(): Promise<Commodity[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('commodities')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (
        data?.map((row) => ({
          id: row.id,
          name: row.name,
          value: parseFloat(row.value),
          change: parseFloat(row.change),
          unit: row.unit,
          icon: row.icon,
        })) || []
      );
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getCityRates(): Promise<CityRate[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('city_rates')
        .select('*')
        .order('city', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (
        data?.map((row) => ({
          id: row.id,
          city: row.city,
          gold22k: parseFloat(row.gold_22k),
          gold24k: parseFloat(row.gold_24k),
          silver: parseFloat(row.silver),
          change22k: parseFloat(row.change_22k),
          change24k: parseFloat(row.change_24k),
          changeSilver: parseFloat(row.change_silver),
        })) || []
      );
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getMarketDataByCategory(
    category: 'stocks' | 'commodities' | 'forex' | 'crypto'
  ): Promise<MarketData[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('market_data')
        .select('*')
        .eq('category', category)
        .order('symbol', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (
        data?.map((row) => ({
          id: row.id,
          symbol: row.symbol,
          name: row.name,
          price: parseFloat(row.price),
          change: parseFloat(row.change),
          changePercent: parseFloat(row.change_percent),
          volume: row.volume || '',
          marketCap: row.market_cap || '',
        })) || []
      );
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getNewsArticles(limit: number = 20): Promise<NewsArticle[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(limit);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (
        data?.map((row) => {
          const publishedDate = new Date(row.published_at);
          const now = new Date();
          const diffMs = now.getTime() - publishedDate.getTime();
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          const diffDays = Math.floor(diffHours / 24);

          let timeAgo = '';
          if (diffDays > 0) {
            timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
          } else if (diffHours > 0) {
            timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
          } else {
            timeAgo = 'Just now';
          }

          return {
            id: row.id,
            title: row.title,
            source: row.source,
            time: timeAgo,
            category: row.category,
            image: row.image,
            alt: row.alt,
            impact: row.impact as 'high' | 'medium' | 'low',
            excerpt: row.excerpt,
            url: row.url || 'https://www.moneymine.in/news',
          };
        }) || []
      );
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  // Real-time subscription methods
  subscribeToMarketIndices(
    callback: (indices: MarketIndex[]) => void
  ): () => void {
    const supabase = createClient();
    
    const channel = supabase
      .channel('market_indices_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_indices',
        },
        async () => {
          const data = await this.getMarketIndices();
          callback(data);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  subscribeToCommodities(
    callback: (commodities: Commodity[]) => void
  ): () => void {
    const supabase = createClient();
    
    const channel = supabase
      .channel('commodities_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'commodities',
        },
        async () => {
          const data = await this.getCommodities();
          callback(data);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  subscribeToCityRates(
    callback: (cityRates: CityRate[]) => void
  ): () => void {
    const supabase = createClient();
    
    const channel = supabase
      .channel('city_rates_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'city_rates',
        },
        async () => {
          const data = await this.getCityRates();
          callback(data);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  subscribeToMarketData(
    category: 'stocks' | 'commodities' | 'forex' | 'crypto',
    callback: (marketData: MarketData[]) => void
  ): () => void {
    const supabase = createClient();
    
    const channel = supabase
      .channel(`market_data_${category}_changes`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_data',
          filter: `category=eq.${category}`,
        },
        async () => {
          const data = await this.getMarketDataByCategory(category);
          callback(data);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  subscribeToNewsArticles(
    callback: (articles: NewsArticle[]) => void,
    limit: number = 20
  ): () => void {
    const supabase = createClient();
    
    const channel = supabase
      .channel('news_articles_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'news_articles',
        },
        async () => {
          const data = await this.getNewsArticles(limit);
          callback(data);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};
